import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosClient from '../axios/api.js';

// Thunks backing the `VendorDatabase` route. The material-code lookups, the
// duplicate/similarity checks and the vendor-progress poll are shared with the
// `DeletedVendor` route, which imports them from here rather than redefining
// them — only the add/edit wizard endpoints differ between the two pages
// (`/api/modular/*` here vs the legacy `/api/*` there).

const asMessage = (error, fallback) =>
  error.response?.data?.error ||
  error.response?.data?.message ||
  error.message ||
  fallback;

// ------------------------------------------------------------- vendor list

export const fetchVendors = createAsyncThunk(
  'vendors/fetchVendors',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get('/vendordata');
      return response.data;
    } catch (error) {
      return rejectWithValue(
        asMessage(error, 'Error fetching vendor data. Please try again later.')
      );
    }
  }
);

export const searchVendors = createAsyncThunk(
  'vendors/searchVendors',
  async (params, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get('/api/vendor-search', { params: { ...params, status: 'Active' } });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Error searching vendors.'));
    }
  }
);

export const applyVendorFilter = createAsyncThunk(
  'vendors/applyVendorFilter',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/api/applyvendorfilter', payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to apply vendor filter'));
    }
  }
);

export const fetchVendorById = createAsyncThunk(
  'vendors/fetchVendorById',
  async (vendorId, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/getVendor/${vendorId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch vendor'));
    }
  }
);

export const deleteVendor = createAsyncThunk(
  'vendors/deleteVendor',
  async (idOrName, { rejectWithValue }) => {
    try {
      await axiosClient.delete(`/api/vendor/${idOrName}`);
      return idOrName;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Delete company error'));
    }
  }
);

export const updateVendorCompany = createAsyncThunk(
  'vendors/updateVendorCompany',
  async ({ id, editedCompany }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.put(`/api/company/${id}`, editedCompany);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Edit company error'));
    }
  }
);


export const moveVendorCompany = createAsyncThunk(
  'vendors/moveVendorCompany',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosClient.put(`/api/company/move/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Move company error'));
    }
  }
);

export const uploadVendorFile = createAsyncThunk(
  'vendors/uploadVendorFile',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Upload error'));
    }
  }
);

// ------------------------------------------------- shared: duplicate checks

export const checkVendorName = createAsyncThunk(
  'vendors/checkVendorName',
  async (formValues, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/api/checkvendorname', { formValues });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Check company name error'));
    }
  }
);

export const checkVendorSimilarity = createAsyncThunk(
  'vendors/checkVendorSimilarity',
  async (companyName, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/api/check-similarity', {
        type: 'vendor',
        companyName,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Similarity check error'));
    }
  }
);

export const addVendorNotification = createAsyncThunk(
  'vendors/addVendorNotification',
  async (vendor_name, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/api/addnotification', { vendor_name });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to queue notification'));
    }
  }
);

// Polled while a vendor add/edit runs in the background.
export const fetchVendorProgress = createAsyncThunk(
  'vendors/fetchVendorProgress',
  async (vendorName, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get('/api/vendor-progress', {
        params: { vendor_name: vendorName },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch vendor progress'));
    }
  }
);

// -------------------------------------------- shared: material-code lookups

export const fetchProductOptions = createAsyncThunk(
  'vendors/fetchProductOptions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get('/api/material-code/material_code_product');
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch product options'));
    }
  }
);

export const registerCustomProduct = createAsyncThunk(
  'vendors/registerCustomProduct',
  async (product, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/api/material-code/product', { product });
      return response.data;
    } catch (error) {
      // The vendor dialogs register every non-default product on each save, so one that is
      // already in the product list answers 409. For a vendor save that is fine: it exists.
      if (error?.response?.status === 409) return { product, alreadyExists: true };
      return rejectWithValue(asMessage(error, 'Failed to register custom product'));
    }
  }
);

export const fetchCategoryList = createAsyncThunk(
  'vendors/fetchCategoryList',
  async (product, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post(
        '/api/vendor-material-code/material_code_category',
        { product }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Error fetching category list'));
    }
  }
);

export const fetchCategoryFilterList = createAsyncThunk(
  'vendors/fetchCategoryFilterList',
  async (product, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post(
        '/api/vendor-material-code/material_code_category_filter',
        { product }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Error fetching category filter list'));
    }
  }
);

export const fetchMaterialList = createAsyncThunk(
  'vendors/fetchMaterialList',
  async (product, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(
        '/api/vendor-material-code/material_code_material',
        { params: { product } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Error fetching material list'));
    }
  }
);

export const fetchMaterialFilterList = createAsyncThunk(
  'vendors/fetchMaterialFilterList',
  async (product, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(
        '/api/vendor-material-code/material_code_material_filter',
        { params: { product } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Error fetching material filter list'));
    }
  }
);

// --------------------------------- shared: per-vendor material-code table

export const fetchProductFilterOptions = createAsyncThunk(
  'vendors/fetchProductFilterOptions',
  async (vendorId, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/vendormaterialcodedata-filters/${vendorId}`);
      return response.data?.options ?? null;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch product filter options'));
    }
  }
);

export const fetchMaterialCodeData = createAsyncThunk(
  'vendors/fetchMaterialCodeData',
  async ({ vendorId, page, limit, filters }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/vendormaterialcodedata-paged/${vendorId}`, {
        params: {
          page,
          limit,
          filters: filters && Object.keys(filters).length ? JSON.stringify(filters) : undefined,
        },
      });
      const materialCode = response.data?.material_code || [];
      const safeMaterialCode = Array.isArray(materialCode) ? materialCode : [];
      const totalValue = Number(response.data?.total);
      return {
        materialCode: safeMaterialCode,
        total: Number.isFinite(totalValue) ? totalValue : safeMaterialCode.length,
      };
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch material code data'));
    }
  }
);

// --------------------------------------------- add/edit wizard (modular)


export const addVendorModular = createAsyncThunk(
  'vendors/addVendorModular',
  async ({ allData, country, company_type }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/api/modular/addvendor', {
        allData,
        country,
        company_type,
      });
      return { status: response.status, data: response.data };
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to add vendor'));
    }
  }
);

export const editVendorModular = createAsyncThunk(
  'vendors/editVendorModular',
  async ({ allData, id, country, company_type }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/api/modular/editvendor', {
        allData,
        id,
        country,
        company_type,
      });
      return { status: response.status, data: response.data };
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to edit vendor'));
    }
  }
);

export const cancelAddVendorModular = createAsyncThunk(
  'vendors/cancelAddVendorModular',
  async (allData, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/api/modular/canceladdvendor', { allData });
      return { status: response.status, data: response.data };
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to cancel vendor add'));
    }
  }
);

export const backVendorModular = createAsyncThunk(
  'vendors/backVendorModular',
  async ({ allData, country, id }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/api/modular/backvendor', { allData, country, id });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Error sending product data'));
    }
  }
);

export const nextVendorModular = createAsyncThunk(
  'vendors/nextVendorModular',
  async ({ allData, country, id }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/api/modular/nextvendor', { allData, country, id });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Error sending product data'));
    }
  }
);

export const nextVendorProductQueueModular = createAsyncThunk(
  'vendors/nextVendorProductQueueModular',
  async ({ vendor_name, product }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post('/api/modular/nextvendorproductqueue', {
        vendor_name,
        product,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Error sending product data'));
    }
  }
);


// The edit dialog reads its product list from a different endpoint than the
// add wizard's material-code product list.
export const fetchVendorProductList = createAsyncThunk(
  'vendors/fetchVendorProductList',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get('/api/product-list');
      return response.data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Error fetching products'));
    }
  }
);

// -------------------------------------------------- shared: template URLs

const TEMPLATE_ENDPOINTS = {
  allContacts: '/fetch-allcontacts-documentformat',
  address: '/fetch-address-document-format',
  clients: '/fetch-clients-documentformat',
};

export const fetchVendorTemplateUrl = createAsyncThunk(
  'vendors/fetchVendorTemplateUrl',
  async (kind, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(TEMPLATE_ENDPOINTS[kind]);
      return { kind, url: response.data };
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to fetch template URL'));
    }
  }
);

const initialState = {
  // Vendor list
  vendors: [],
  loading: true,
  error: null,
  searchResults: [],
  searchLoading: false,
  searchTotal: 0,

  // Wizard reference data
  productOptions: [],
  categoryList: [],
  materialList: [],
  categoryOptions: [],
  materialOptions: [],

  // Per-vendor material-code table
  materialData: [],
  productTotal: 0,
  productLoading: false,
  productFilterOptions: null,
  productFilterOptionsLoading: false,

  // Wizard flight state
  vendorProgress: null,
  similarityMatches: null,
  vendorEdit: null,

  templateUrls: { allContacts: '', address: '', clients: '' },
};

const vendorSlice = createSlice({
  name: 'vendors',
  initialState,
  reducers: {
    setVendors: (state, action) => {
      state.vendors = action.payload;
    },
    setVendorLoading: (state, action) => {
      state.loading = action.payload;
    },
    setCategoryList: (state, action) => {
      state.categoryList = action.payload;
    },
    setMaterialList: (state, action) => {
      state.materialList = action.payload;
    },
    setCategoryOptions: (state, action) => {
      state.categoryOptions = action.payload;
    },
    setMaterialOptions: (state, action) => {
      state.materialOptions = action.payload;
    },
    setProductOptionsLocal: (state, action) => {
      state.productOptions = action.payload;
    },
    setMaterialData: (state, action) => {
      state.materialData = action.payload;
    },
    setProductFilterOptions: (state, action) => {
      state.productFilterOptions = action.payload;
    },
    setProductTotal: (state, action) => {
      state.productTotal = action.payload;
    },
    setProductFilterOptionsLoading: (state, action) => {
      state.productFilterOptionsLoading = action.payload;
    },
    // Clears the per-vendor material-code table when its dialog opens/closes.
    resetProductTable: (state) => {
      state.materialData = [];
      state.productTotal = 0;
      state.productFilterOptions = null;
      state.productFilterOptionsLoading = false;
    },
    clearVendorError: (state) => {
      state.error = null;
    },
    resetVendorState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVendors.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVendors.fulfilled, (state, action) => {
        state.loading = false;
        state.vendors = action.payload;
      })
      .addCase(fetchVendors.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(searchVendors.pending, (state) => {
        state.searchLoading = true;
        state.error = null;
      })
      .addCase(searchVendors.fulfilled, (state, action) => {
        state.searchLoading = false;
        state.searchResults = action.payload.data;
        state.searchTotal = action.payload.pagination.total;
      })
      .addCase(searchVendors.rejected, (state, action) => {
        state.searchLoading = false;
        state.error = action.payload;
        state.searchResults = [];
        state.searchTotal = 0;
      })

      .addCase(applyVendorFilter.pending, (state) => {
        state.loading = true;
      })
      .addCase(applyVendorFilter.fulfilled, (state, action) => {
        state.loading = false;
        state.vendors = action.payload;
      })
      .addCase(applyVendorFilter.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchVendorById.fulfilled, (state, action) => {
        state.vendorEdit = action.payload;
      })

      .addCase(fetchVendorProgress.fulfilled, (state, action) => {
        state.vendorProgress = action.payload;
      })
      .addCase(checkVendorSimilarity.fulfilled, (state, action) => {
        state.similarityMatches = action.payload;
      })

      // Reference data
      .addCase(fetchProductOptions.fulfilled, (state, action) => {
        state.productOptions = action.payload;
      })
      .addCase(fetchCategoryList.fulfilled, (state, action) => {
        state.categoryList = action.payload;
      })
      .addCase(fetchCategoryFilterList.fulfilled, (state, action) => {
        state.categoryOptions = action.payload;
      })
      .addCase(fetchMaterialList.fulfilled, (state, action) => {
        state.materialList = action.payload;
      })
      .addCase(fetchMaterialFilterList.fulfilled, (state, action) => {
        state.materialOptions = action.payload;
      })

      // Per-vendor material-code table
      .addCase(fetchProductFilterOptions.pending, (state) => {
        state.productFilterOptionsLoading = true;
      })
      .addCase(fetchProductFilterOptions.fulfilled, (state, action) => {
        state.productFilterOptionsLoading = false;
        state.productFilterOptions = action.payload;
      })
      .addCase(fetchProductFilterOptions.rejected, (state) => {
        state.productFilterOptionsLoading = false;
        state.productFilterOptions = null;
      })
      .addCase(fetchMaterialCodeData.pending, (state) => {
        state.productLoading = true;
      })
      .addCase(fetchMaterialCodeData.fulfilled, (state, action) => {
        state.productLoading = false;
        state.materialData = action.payload.materialCode;
        state.productTotal = action.payload.total;
      })
      .addCase(fetchMaterialCodeData.rejected, (state, action) => {
        state.productLoading = false;
        state.error = action.payload;
      })

      .addCase(fetchVendorTemplateUrl.fulfilled, (state, action) => {
        state.templateUrls[action.payload.kind] = action.payload.url;
      });
  },
});

export const {
  setVendors,
  setVendorLoading,
  setCategoryList,
  setMaterialList,
  setCategoryOptions,
  setMaterialOptions,
  setProductOptionsLocal,
  setMaterialData,
  setProductFilterOptions,
  setProductTotal,
  setProductFilterOptionsLoading,
  resetProductTable,
  clearVendorError,
  resetVendorState,
} = vendorSlice.actions;

export default vendorSlice.reducer;
